import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, User, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
const riskBg = (score) => score >= 50 ? '#FFEBEE' : score >= 35 ? '#FFF3E0' : '#E8F5E9';
const riskTextColor = (score) => score >= 50 ? '#C62828' : score >= 35 ? '#E65100' : '#2E7D32';
const formatAudM = (m) => {
  if (m == null) return '-';
  return m >= 1000 ? `$${(m / 1000).toFixed(1)}B` : `$${m.toLocaleString()}M`;
};

function KpiCard({ label, value, trend, color }) {
  const arrow = trend === 'up' || trend === 'worsening' ? '\u25B2' : trend === 'down' || trend === 'improving' ? '\u25BC' : '\u2014';
  const trendColor = color === 'green' ? '#2E7D32' : color === 'red' ? '#F44336' : '#FF9800';
  return (
    <div style={{ background: '#fff', border: '1px solid #E8ECEF', borderRadius: 8, padding: 16, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} data-testid={`kpi-${label?.replace(/\s+/g, '-').toLowerCase()}`}>
      <div style={{ fontSize: 11, color: '#4E6974', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#0D3B4F', lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 12, color: trendColor, marginTop: 6 }}>{arrow} {capitalize(trend)}</div>
    </div>
  );
}

function ScoreBar({ label, score }) {
  const color = score < 35 ? '#2E7D32' : score < 50 ? '#FF9800' : '#F44336';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }} data-testid={`score-bar-${label?.replace(/\s+/g, '-').toLowerCase()}`}>
      <div style={{ width: 210, fontSize: 13, color: '#4E6974', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 12, background: '#E0E0E0', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: 6 }} />
      </div>
      <div style={{ width: 36, textAlign: 'center', fontWeight: 700, color: '#0D3B4F', fontSize: 13 }}>{score}</div>
    </div>
  );
}

function SectorCard({ name, kpis, narrative, opportunities, risks, relevance }) {
  return (
    <div style={{ border: '1px solid #E8ECEF', borderRadius: 8, padding: 22, marginBottom: 20 }} data-testid={`sector-${name?.replace(/\s+/g, '-').toLowerCase()}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0D3B4F', margin: 0 }}>{name}</h3>
        {relevance && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 4, color: '#fff', background: relevance === 'high' ? '#0097A7' : relevance === 'medium' ? '#4E6974' : '#B0BEC5' }}>
            {relevance} relevance
          </span>
        )}
      </div>
      {kpis?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
          {kpis.map(kpi => (
            <div key={kpi.label} style={{ background: '#F5F7FA', border: '1px solid #E8ECEF', borderRadius: 6, padding: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#4E6974', marginBottom: 4 }}>{kpi.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#0D3B4F' }}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}
      <p style={{ fontSize: 13, color: '#4E6974', lineHeight: 1.7, marginBottom: 12 }}>{narrative}</p>
      {(opportunities || risks) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {opportunities && !opportunities.startsWith('Limited sector-specific') && (
            <div style={{ background: '#E8F5E9', padding: 14, borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#2E7D32', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Opportunities</div>
              <p style={{ fontSize: 12, color: '#0D3B4F', lineHeight: 1.6, margin: 0 }}>{opportunities}</p>
            </div>
          )}
          {risks && !risks.startsWith('Sector-specific risks are minimal') && (
            <div style={{ background: '#FFF3E0', padding: 14, borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#FF9800', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Risks</div>
              <p style={{ fontSize: 12, color: '#0D3B4F', lineHeight: 1.6, margin: 0 }}>{risks}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TradeTable({ data, countryName, direction }) {
  if (!data?.length) return null;
  const title = direction === 'to'
    ? `What ${countryName} exports to Australia`
    : `What Australia exports to ${countryName}`;
  return (
    <>
      <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{title}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 24 }}>
        <thead>
          <tr>
            {['Category', 'Value (AUD)', 'YoY change', 'Key products'].map(h => (
              <th key={h} style={{ background: '#0D3B4F', color: '#fff', padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.category} style={{ background: i % 2 === 0 ? '#fff' : '#F5F5F5' }}>
              <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#0D3B4F', fontWeight: 600 }}>{row.category}</td>
              <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{formatAudM(row.value_aud_m)}</td>
              <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: row.yoy_change < 0 ? '#F44336' : '#2E7D32', fontWeight: 600 }}>{row.yoy_change >= 0 ? '+' : ''}{row.yoy_change}%</td>
              <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{row.key_products}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const ReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [standardReport, setStandardReport] = useState(null);
  const [personalisedReport, setPersonalisedReport] = useState(null);
  const [hasPersonalisation, setHasPersonalisation] = useState(false);
  const [viewMode, setViewMode] = useState('my');
  const [loadingPersonalised, setLoadingPersonalised] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const stdRes = await api.getReportDetail(reportId);
      setStandardReport(stdRes.data);
      setReport(stdRes.data);
      setLoadingPersonalised(true);
      try {
        const persRes = await api.getPersonalisedReport(reportId);
        if (persRes.data.has_personalisation && persRes.data.report) {
          setPersonalisedReport(persRes.data.report);
          setHasPersonalisation(true);
          setReport(persRes.data.report);
          setViewMode('my');
        }
      } catch (error) {
        console.error('Failed to load personalised report:', error);
      } finally {
        setLoadingPersonalised(false);
      }
    } catch (e) {
      console.error('Failed to load report:', e);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleView = (mode) => {
    setViewMode(mode);
    setReport(mode === 'my' && personalisedReport ? personalisedReport : standardReport);
  };

  const handleDownload = () => {
    if (!report) return;
    const a = document.createElement('a');
    a.href = api.downloadReportPdf(reportId);
    a.download = `${report.title?.replace(/\s+/g, '_') || 'report'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Report PDF downloaded');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#E8E8E8] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#0097A7]" />
    </div>
  );

  if (!report) return (
    <div className="min-h-screen bg-[#E8E8E8] flex items-center justify-center">
      <p className="text-[#4E6974]">Report not found</p>
    </div>
  );

  const c = report.content || {};
  const isDeepDive = report.report_type === 'country_deep_dive';

  /* ── Non-deep-dive fallback ── */
  if (!isDeepDive) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <NavBar />
        <main className="pt-24 pb-8 max-w-[880px] mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate('/reports')} className="mb-4 text-[#4E6974]" data-testid="back-to-reports-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to reports
          </Button>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div style={{ background: '#0A2533', padding: '32px 40px' }}>
              <h1 className="text-2xl font-bold text-white" data-testid="report-title">{report.title}</h1>
              <p className="text-[#4DD0E1] text-sm mt-2">{formatDate(report.created_at)} | {report.countries_covered?.join(', ')}</p>
            </div>
            <div style={{ padding: '32px 40px' }}>
              <h2 className="text-lg font-bold text-[#0D3B4F] mb-3">Executive summary</h2>
              {report.executive_summary?.split('\n\n').map((p, i) => (
                <p key={`es-${i}`} className="text-sm text-[#4E6974] leading-relaxed mb-3">{p}</p>
              ))}
              {Array.isArray(c.recommendations) && c.recommendations.length > 0 && (
                <>
                  <h2 className="text-lg font-bold text-[#0D3B4F] mt-6 mb-3">Recommendations</h2>
                  {c.recommendations.map((r, i) => (
                    <div key={`rec-${i}`} className="flex gap-3 p-3 bg-[#E8F5E9] rounded-lg mb-2">
                      <span className="font-bold text-[#2E7D32]">{i + 1}.</span>
                      <p className="text-sm text-[#0D3B4F] leading-relaxed">{r}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="text-center mt-4">
            <Button onClick={handleDownload} className="bg-[#00707C] hover:bg-[#005F6A] text-white" data-testid="download-report-btn">
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          </div>
        </main>
      </div>
    );
  }

  /* ── Deep dive data bindings ── */
  const cover = c.cover || {};
  const riskAnalysis = c.risk_analysis || {};
  const tradeEcon = c.trade_economic || {};
  const portLogistics = c.port_logistics || {};
  const scenariosData = c.scenarios || {};
  const recsData = c.recommendations || {};
  const execSummary = c.executive_summary || report.executive_summary || '';
  const countryName = cover.country_name || report.title || '';
  const riskScore = cover.risk_score;
  const riskLevel = cover.risk_level || '';
  const trendText = cover.trend || '';
  const quarter = cover.quarter || c.quarter || '';

  return (
    <div style={{ minHeight: '100vh', background: '#E8E8E8', fontFamily: "Calibri, 'Trebuchet MS', sans-serif" }}>
      <NavBar />
      <main style={{ paddingTop: 96, paddingBottom: 40 }}>

        {/* ── Controls bar ── */}
        <div style={{ maxWidth: 880, margin: '0 auto 12px', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Button variant="ghost" onClick={() => navigate('/reports')} data-testid="back-to-reports-btn" className="text-[#4E6974]">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to reports
          </Button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {hasPersonalisation && (
              <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #E0E0E0' }} data-testid="report-view-toggle">
                <button onClick={() => toggleView('my')} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer', background: viewMode === 'my' ? '#0097A7' : '#fff', color: viewMode === 'my' ? '#fff' : '#4E6974' }} data-testid="toggle-my-report">
                  <User style={{ width: 14, height: 14 }} /> My report
                </button>
                <button onClick={() => toggleView('standard')} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer', background: viewMode === 'standard' ? '#0097A7' : '#fff', color: viewMode === 'standard' ? '#fff' : '#4E6974' }} data-testid="toggle-standard-report">
                  <FileText style={{ width: 14, height: 14 }} /> Standard report
                </button>
              </div>
            )}
            <Button onClick={handleDownload} data-testid="download-report-btn" className="bg-[#00707C] hover:bg-[#005F6A] text-white">
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
          </div>
        </div>

        {/* ── The Document ── */}
        <div style={{ maxWidth: 880, margin: '0 auto', background: '#fff', boxShadow: '0 4px 32px rgba(0,0,0,0.10)' }} data-testid="report-document">

          {/* COVER PAGE */}
          <div style={{ background: '#0A2533', padding: '56px 56px 48px' }}>
            <div style={{ color: '#0097A7', fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase' }}>
              Nuvanta Solutions&ensp;|&ensp;TradePath
            </div>
            <div style={{ color: '#FFFFFF', fontFamily: "'Trebuchet MS', sans-serif", fontSize: 64, fontWeight: 700, marginTop: 28, lineHeight: 1.1 }} data-testid="report-title">
              {countryName}
            </div>
            <div style={{ color: '#4DD0E1', fontSize: 24, marginTop: 10 }}>
              Country deep dive risk assessment
            </div>
            <div style={{ color: '#4DD0E1', fontSize: 16, marginTop: 6 }}>
              {quarter}
            </div>
          </div>
          <div style={{ padding: '44px 56px 40px' }}>
            {riskScore != null && (
              <>
                <div style={{ color: '#4E6974', fontSize: 13, marginBottom: 6 }}>Overall risk score</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 80, fontWeight: 700, color: '#0D3B4F', lineHeight: 1 }} data-testid="cover-risk-score">{riskScore}</span>
                  <span style={{ fontSize: 28, color: '#4E6974' }}>/ 100</span>
                </div>
                <div style={{ display: 'inline-block', marginTop: 12, padding: '5px 18px', borderRadius: 20, background: riskBg(riskScore), color: riskTextColor(riskScore), fontSize: 14, fontWeight: 700 }}>
                  {capitalize(riskLevel)} risk{trendText ? ` \u2014 ${capitalize(trendText)}` : ''}
                </div>
              </>
            )}
            <div style={{ marginTop: 20, fontSize: 12, color: '#4E6974', lineHeight: 2 }}>
              {cover.audience && <>Audience: {cover.audience}<br /></>}
              {cover.sectors_covered && <>Sectors: {cover.sectors_covered}<br /></>}
              {cover.report_date && <>Generated: {cover.report_date}</>}
              {cover.refresh_frequency && <> | {cover.refresh_frequency}</>}
            </div>
            <div style={{ height: 2, background: '#0097A7', margin: '28px 0 16px' }} />
            <div style={{ textAlign: 'center', fontSize: 11, color: '#4E6974' }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>CONFIDENTIAL</div>
              <div>&copy; 2026 Nuvanta Solutions Pty Ltd. All rights reserved.</div>
            </div>
          </div>

          {/* EXECUTIVE SUMMARY */}
          <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
            <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
              Executive summary
            </h2>
            <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
            {execSummary.split('\n\n').map((p, i) => (
              <p key={`es-${i}`} style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 16 }}>{p}</p>
            ))}
            {c.bottom_line && (
              <div style={{ borderLeft: '4px solid #0097A7', background: '#F5F5F5', padding: 20, borderRadius: 4, marginBottom: 28 }}>
                <div style={{ fontWeight: 700, color: '#0D3B4F', fontSize: 14, marginBottom: 8 }}>
                  Bottom line for Australian businesses
                </div>
                <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, margin: 0 }}>{c.bottom_line}</p>
              </div>
            )}
            {c.kpi_cards?.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(c.kpi_cards.length, 4)}, 1fr)`, gap: 14 }}>
                {c.kpi_cards.map(kpi => <KpiCard key={kpi.label} {...kpi} />)}
              </div>
            )}
          </div>

          {/* RISK SCORE ANALYSIS */}
          {riskAnalysis.dimensions && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Risk score analysis
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              {riskAnalysis.intro && (
                <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 24 }}>{riskAnalysis.intro}</p>
              )}
              <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
                Risk dimension breakdown
              </h3>
              <div style={{ marginBottom: 28 }}>
                {Object.values(riskAnalysis.dimensions).map(dim => (
                  <ScoreBar key={dim.label} label={dim.label} score={dim.score} />
                ))}
              </div>

              {riskAnalysis.comparison_table?.length > 0 && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
                    Comparative positioning
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 }} data-testid="comparison-table">
                    <thead>
                      <tr>
                        {['Country', 'Risk score', 'Trend', 'FTA rate range', 'Relative wage index', 'Best for'].map(h => (
                          <th key={h} style={{ background: '#0D3B4F', color: '#fff', padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {riskAnalysis.comparison_table.map((row, i) => (
                        <tr key={row.country_code || row.country} style={{ background: i % 2 === 0 ? '#fff' : '#F5F5F5' }}>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#0D3B4F', fontWeight: i === 0 ? 700 : 400 }}>{row.country}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{row.risk_score}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{capitalize(row.trend)}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{row.fta_range}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{row.wage_index?.toFixed(2)}x</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{row.best_for}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: 11, fontStyle: 'italic', color: '#4E6974', marginBottom: 24 }}>
                    FTA rates shown are indicative preferential ranges under the most favourable applicable agreement. Wage index is based on average manufacturing sector wages in USD terms.
                  </p>
                </>
              )}
              {riskAnalysis.analyst_note && (
                <div style={{ borderLeft: '4px solid #0097A7', background: '#F5F5F5', padding: 20, borderRadius: 4 }}>
                  <div style={{ fontWeight: 700, color: '#0D3B4F', fontSize: 14, marginBottom: 8 }}>Analyst note</div>
                  <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, margin: 0 }}>{riskAnalysis.analyst_note}</p>
                </div>
              )}
            </div>
          )}

          {/* TRADE AND ECONOMIC ANALYSIS */}
          {tradeEcon.bilateral_overview && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Trade and economic analysis
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Bilateral trade overview</h3>
              <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 24 }}>{tradeEcon.bilateral_overview}</p>
              <TradeTable data={tradeEcon.exports_to_australia} countryName={countryName} direction="to" />
              <TradeTable data={tradeEcon.australia_exports_to} countryName={countryName} direction="from" />
              {tradeEcon.trade_agreement_detail && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Trade agreement landscape</h3>
                  <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 20 }}>{tradeEcon.trade_agreement_detail}</p>
                </>
              )}
              {tradeEcon.tariff_insight && (
                <div style={{ borderLeft: '4px solid #0097A7', background: '#F5F5F5', padding: 20, borderRadius: 4 }}>
                  <div style={{ fontWeight: 700, color: '#0D3B4F', fontSize: 14, marginBottom: 8 }}>Tariff insight</div>
                  <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, margin: 0 }}>{tradeEcon.tariff_insight}</p>
                </div>
              )}
            </div>
          )}

          {/* SECTOR RISK ANALYSIS */}
          {c.sector_analysis?.length > 0 && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Sector risk analysis
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 24 }}>
                Risk profiles vary significantly by sector. Below is an assessment of each primary sector based on {countryName}-specific conditions and data.
              </p>
              {c.sector_analysis.map(sector => (
                <SectorCard key={sector.name} {...sector} />
              ))}
            </div>
          )}

          {/* PORT AND LOGISTICS INTELLIGENCE */}
          {portLogistics.intro && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Port and logistics intelligence
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 20 }}>{portLogistics.intro}</p>
              {portLogistics.ports_table?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }} data-testid="ports-table">
                  <thead>
                    <tr>
                      {['Port', 'Status', 'Avg wait (days)', 'Dwell time (days)', 'Utilisation', 'Freight rate (AUD/TEU)', 'Transit to Australia'].map(h => (
                        <th key={h} style={{ background: '#0D3B4F', color: '#fff', padding: '10px 10px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {portLogistics.ports_table.map((p, i) => (
                      <tr key={p.name} style={{ background: i % 2 === 0 ? '#fff' : '#F5F5F5' }}>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8', color: '#0D3B4F', fontWeight: 600 }}>{p.name}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8' }}>
                          <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: p.status === 'congested' ? '#FF9800' : '#2E7D32' }}>
                            {p.status === 'congested' ? 'Congestion warning' : 'Operational'}
                          </span>
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{p.vessel_wait_days}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{p.dwell_time_days}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{p.utilisation_pct}%</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>${p.freight_rate_teu_aud?.toLocaleString()}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{p.transit_days_to_au} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {portLogistics.port_narratives?.filter(pn => pn.alert).map(pn => (
                <div key={pn.port} style={{ borderLeft: '4px solid #FF9800', background: '#FFF8E1', padding: 20, borderRadius: 4, marginBottom: 24 }}>
                  <div style={{ fontWeight: 700, color: '#E65100', fontSize: 14, marginBottom: 8 }}>Port alert: {pn.port}</div>
                  <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, margin: 0 }}>{pn.narrative}</p>
                  {pn.alternative && <p style={{ fontSize: 12, color: '#E65100', marginTop: 8, fontWeight: 600 }}>{pn.alternative}</p>}
                </div>
              ))}
              {portLogistics.freight_trends && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Freight rate trends</h3>
                  <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, margin: 0 }}>{portLogistics.freight_trends}</p>
                </>
              )}
            </div>
          )}

          {/* KEY DEVELOPMENTS AND RISK SIGNALS */}
          {(c.positive_signals?.length > 0 || c.risk_signals?.length > 0) && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Key developments and risk signals
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 24 }}>
                TradePath monitors over 200 signal sources across government policy announcements, economic data releases, trade negotiations, infrastructure developments, and geopolitical events.
              </p>
              {c.positive_signals?.length > 0 && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Positive signals</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 28 }}>
                    <thead>
                      <tr>
                        {['Impact', 'Signal', 'Category', 'Date'].map(h => (
                          <th key={h} style={{ background: '#0D3B4F', color: '#fff', padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {c.positive_signals.map((s, i) => (
                        <tr key={`pos-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#F5F5F5' }}>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#FF9800', fontSize: 16, letterSpacing: 1 }}>{'★'.repeat(s.impact)}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{s.description}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{s.category}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974', whiteSpace: 'nowrap' }}>{s.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {c.risk_signals?.length > 0 && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Risk signals to monitor</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Severity', 'Signal', 'Category', 'Date'].map(h => (
                          <th key={h} style={{ background: '#0D3B4F', color: '#fff', padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {c.risk_signals.map((s, i) => {
                        const sevColor = s.severity === 'HIGH' ? '#F44336' : s.severity === 'MEDIUM' ? '#FF9800' : '#2E7D32';
                        return (
                          <tr key={`risk-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#F5F5F5' }}>
                            <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8' }}>
                              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: sevColor }}>{s.severity}</span>
                            </td>
                            <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{s.description}</td>
                            <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974' }}>{s.category}</td>
                            <td style={{ padding: '9px 12px', borderBottom: '1px solid #E8E8E8', color: '#4E6974', whiteSpace: 'nowrap' }}>{s.date}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* SCENARIO PLANNING */}
          {scenariosData.scenarios?.length > 0 && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Scenario planning
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              {scenariosData.intro && (
                <p style={{ fontSize: 14, color: '#4E6974', lineHeight: 1.7, marginBottom: 24 }}>{scenariosData.intro}</p>
              )}
              {scenariosData.scenarios.map(s => (
                <div key={s.name} style={{ border: '1px solid #E8ECEF', borderRadius: 8, padding: 24, marginBottom: 16 }} data-testid={`scenario-${s.name?.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0D3B4F', margin: 0 }}>{s.name}</h3>
                    <span style={{ background: '#0D3B4F', color: '#fff', padding: '4px 14px', borderRadius: 14, fontSize: 12, fontWeight: 700 }}>{s.probability}% probability</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#4E6974', lineHeight: 1.7, marginBottom: 10 }}>
                    <strong style={{ color: '#0D3B4F' }}>What happens:</strong> {s.what_happens}
                  </p>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#0097A7', marginBottom: 10 }}>
                    <strong style={{ color: '#0D3B4F' }}>Risk score trajectory:</strong> {s.risk_trajectory}
                  </p>
                  <p style={{ fontSize: 13, color: '#4E6974', lineHeight: 1.7, margin: 0 }}>
                    <strong style={{ color: '#0D3B4F' }}>Implication for Australian businesses:</strong> {s.implication}
                  </p>
                </div>
              ))}
              {scenariosData.monitoring_triggers?.length > 0 && (
                <div style={{ borderLeft: '4px solid #0097A7', background: '#F5F5F5', padding: 20, borderRadius: 4 }}>
                  <div style={{ fontWeight: 700, color: '#0D3B4F', fontSize: 14, marginBottom: 10 }}>Recommended monitoring triggers</div>
                  <ul style={{ fontSize: 13, color: '#4E6974', lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
                    {scenariosData.monitoring_triggers.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* RECOMMENDATIONS AND NEXT STEPS */}
          {recsData && (recsData.immediate?.length > 0 || recsData.medium_term?.length > 0 || recsData.strategic?.length > 0) && (
            <div style={{ padding: '40px 56px', borderTop: '1px solid #E0E0E0' }}>
              <h2 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 22, fontWeight: 700, color: '#0D3B4F', marginBottom: 6 }}>
                Recommendations and next steps
              </h2>
              <div style={{ height: 2, background: '#0097A7', marginBottom: 20 }} />
              {recsData.immediate?.length > 0 && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Immediate actions (next 30 days)</h3>
                  {recsData.immediate.map((r, i) => (
                    <div key={`imm-${i}`} style={{ display: 'flex', gap: 12, padding: '12px 16px', background: '#E8F5E9', borderRadius: 6, marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: '#2E7D32', flexShrink: 0 }}>{i + 1}.</span>
                      <p style={{ fontSize: 13, color: '#0D3B4F', lineHeight: 1.7, margin: 0 }}>{r}</p>
                    </div>
                  ))}
                  <div style={{ marginBottom: 24 }} />
                </>
              )}
              {recsData.medium_term?.length > 0 && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Medium-term actions (next 90 days)</h3>
                  {recsData.medium_term.map((r, i) => (
                    <div key={`med-${i}`} style={{ display: 'flex', gap: 12, padding: '12px 16px', background: '#FFF8E1', borderRadius: 6, marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: '#FF9800', flexShrink: 0 }}>{i + 1}.</span>
                      <p style={{ fontSize: 13, color: '#0D3B4F', lineHeight: 1.7, margin: 0 }}>{r}</p>
                    </div>
                  ))}
                  <div style={{ marginBottom: 24 }} />
                </>
              )}
              {recsData.strategic?.length > 0 && (
                <>
                  <h3 style={{ fontSize: 12, fontWeight: 700, color: '#0D3B4F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Strategic considerations (6-12 months)</h3>
                  <ul style={{ fontSize: 13, color: '#4E6974', lineHeight: 1.8, paddingLeft: 20, marginBottom: 0 }}>
                    {recsData.strategic.map((r, i) => (
                      <li key={`str-${i}`} style={{ marginBottom: 10 }}>{r}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* METHODOLOGY */}
          {c.methodology_note && (
            <div style={{ padding: '40px 56px', borderTop: '2px solid #0097A7' }}>
              <h3 style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 16, fontWeight: 700, color: '#0D3B4F', marginBottom: 14 }}>
                Methodology
              </h3>
              <p style={{ fontSize: 12, color: '#4E6974', lineHeight: 1.7, marginBottom: 14 }}>
                {c.methodology_note}
              </p>
              <p style={{ fontSize: 12, color: '#4E6974', lineHeight: 1.7, fontStyle: 'italic', marginBottom: 20 }}>
                Disclaimer: This report is produced by Nuvanta Solutions Pty Ltd for informational purposes only. It does not constitute financial, legal, or procurement advice. Decisions should be made in consultation with qualified professional advisors. Nuvanta Solutions accepts no liability for actions taken based on the content of this report.
              </p>
              <p style={{ textAlign: 'center', fontSize: 11, color: '#4E6974' }}>
                Nuvanta Solutions Pty Ltd | Melbourne | Brisbane | nuvantasolutions.com.au
              </p>
            </div>
          )}
        </div>

        {/* Bottom download */}
        <div style={{ maxWidth: 880, margin: '20px auto 0', textAlign: 'center' }}>
          <Button onClick={handleDownload} data-testid="download-report-btn-bottom" className="bg-[#00707C] hover:bg-[#005F6A] text-white">
            <Download className="h-4 w-4 mr-2" /> Download full report as PDF
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ReportDetailPage;
